import React, { useRef } from 'react';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';

interface InfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
}

/**
 * Reusable info modal component for displaying information
 */
export const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose, title, children }) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const mouseDownOutsideRef = useRef<boolean>(false);

    if (!isOpen) return null;

    const handleMouseDown = (e: React.MouseEvent) => {
        // Track if mousedown happened on the overlay (outside modal content)
        if (e.target === e.currentTarget) {
            mouseDownOutsideRef.current = true;
        } else {
            mouseDownOutsideRef.current = false;
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        // Only close if both mousedown and mouseup happened outside the modal
        if (e.target === e.currentTarget && mouseDownOutsideRef.current) {
            onClose();
        }
        // Reset the flag
        mouseDownOutsideRef.current = false;
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        }} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: theme.borderRadius.md,
                maxWidth: '600px',
                maxHeight: '80vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: theme.spacing.md,
                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                    backgroundColor: theme.colors.primary,
                    flexShrink: 0,
                }}>
                    <h2 style={{
                        ...themedStyles.text.heading,
                        fontSize: theme.typography.fontSize.xl,
                        margin: 0,
                        color: '#ffffff',
                        flex: 1,
                    }}>{title}</h2>
                    <button type="button" onClick={onClose} style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        color: '#ffffff',
                        padding: theme.spacing.xs,
                    }} aria-label="Close">×</button>
                </div>
                <div style={{
                    ...themedStyles.text.body,
                    color: theme.colors.text.primary,
                    lineHeight: 1.6,
                    padding: theme.spacing.xl,
                    overflow: 'auto',
                }}>
                    {children}
                </div>
            </div>
        </div>
    );
};
